/**
 * sublocations.js — deellocaties: vlakken op de kaart (NEN 5740).
 *
 * Gedeeld door de projectkaart en de kaart bij de labopdracht. Op beide kun je tekenen, en
 * beide tonen dezelfde vlakken: wat je op de ene opslaat, verschijnt meteen op de andere.
 *
 * Een vlak is een open ring van [lengtegraad, breedtegraad]-paren (WGS84), net als de x/y van
 * een boring. Welke boringen in een deellocatie liggen wordt niet opgeslagen maar hier
 * uitgerekend: verplaats je een boring, dan verhuist hij vanzelf mee.
 */

import L from 'leaflet';
import { api } from '../api.js';

/** Kleur van een deellocatie: oranje, zodat hij niet verward wordt met de groene selectie. */
export const SUBLOCATION_COLOR = '#c2410c';

/** Venster-event na elke wijziging. detail: { projectId, sublocations } */
export const SUBLOCATIONS_EVENT = 'mv-sublocations-changed';

export {
  pointInRing, inSublocation, sublocationAt, sublocationsAt, nextSublocationName, ringAreaM2,
} from './sublocation-geometry.js';

// ── Opslag ─────────────────────────────────────────────────────────────────

/** Eén ophaalactie per project, gedeeld door alle kaarten op de pagina. */
const cache = new Map();

function publish(projectId, sublocations) {
  cache.set(projectId, Promise.resolve(sublocations));
  window.dispatchEvent(new CustomEvent(SUBLOCATIONS_EVENT, { detail: { projectId, sublocations } }));
  return sublocations;
}

export function loadSublocations(projectId, { refresh = false } = {}) {
  if (!projectId) return Promise.resolve([]);
  if (refresh || !cache.has(projectId)) {
    const p = api.getSublocations(projectId)
      .then((res) => res?.sublocations || [])
      // Een mislukte ophaalactie niet bewaren, anders blijft de kaart leeg tot je herlaadt.
      .catch((e) => { cache.delete(projectId); throw e; });
    cache.set(projectId, p);
  }
  return cache.get(projectId);
}

const byName = (a, b) => a.name.localeCompare(b.name, 'nl', { numeric: true });

export async function saveNewSublocation(projectId, data) {
  const created = await api.createSublocation(projectId, data);
  const list = await loadSublocations(projectId).catch(() => []);
  publish(projectId, [...list.filter((s) => s.id !== created.id), created].sort(byName));
  return created;
}

export async function saveSublocation(projectId, id, data) {
  const updated = await api.updateSublocation(id, data);
  const list = await loadSublocations(projectId).catch(() => []);
  publish(projectId, list.map((s) => (s.id === id ? updated : s)).sort(byName));
  return updated;
}

export async function removeSublocation(projectId, id) {
  await api.deleteSublocation(id);
  const list = await loadSublocations(projectId).catch(() => []);
  publish(projectId, list.filter((s) => s.id !== id));
}

// ── Op de kaart ────────────────────────────────────────────────────────────

const toLatLngs = (ring) => ring.map(([lng, lat]) => [lat, lng]);

/**
 * Teken de deellocaties in een laaggroep. `onClick(sublocation, event)` is optioneel; zonder
 * is het vlak alleen om naar te kijken (de compacte kaart op het overzicht).
 *
 * `bubbling`: laat muisacties op het vlak ook bij de kaart aankomen. Nodig waar slepen over de
 * kaart een kader trekt (de labopdracht): anders begint een kader nooit binnen een vlak.
 */
export function drawSublocations(group, sublocations, { onClick, highlightId = null, bubbling = false } = {}) {
  group.clearLayers();
  for (const s of sublocations || []) {
    if (!(s.geometry?.length >= 3)) continue;
    const on = s.id === highlightId;
    const poly = L.polygon(toLatLngs(s.geometry), {
      color: SUBLOCATION_COLOR, weight: on ? 3 : 2, dashArray: on ? null : '6 4',
      fillColor: SUBLOCATION_COLOR, fillOpacity: on ? 0.16 : 0.07,
      interactive: !!onClick,
      // Standaard is klikken op een vlak geen klik op de kaart: anders sluit Leaflet de popup
      // die je net opent.
      bubblingMouseEvents: bubbling,
    });
    poly.bindTooltip(s.name, { permanent: true, direction: 'center', className: 'mv-dl-label', interactive: false });
    if (onClick) poly.on('click', (e) => onClick(s, e));
    poly.addTo(group);
  }
}

export function sublocationBounds(s) {
  return L.latLngBounds(toLatLngs(s.geometry || []));
}

/**
 * CSS voor de vlakken en het tekenen, voor in de static styles van een kaartonderdeel
 * (de kaart staat in een shadow-DOM, dus globale CSS komt er niet bij).
 */
export const SUBLOCATION_MAP_CSS = `
  .leaflet-tooltip.mv-dl-label { background:none; border:none; box-shadow:none; padding:0;
    font:800 11px/1.2 system-ui,-apple-system,sans-serif; color:${SUBLOCATION_COLOR};
    text-shadow:0 0 3px #fff, 0 0 3px #fff, 0 0 2px #fff; white-space:nowrap; }
  .leaflet-tooltip.mv-dl-label::before { display:none; }
  .mv-dl-drawing { cursor:crosshair !important; }
  /* Tijdens het tekenen is alles op de kaart doorzichtig voor klikken, behalve het eerste
     hoekpunt (afsluiten): anders kun je geen punt zetten op een boring of in een bestaand vlak. */
  .mv-dl-drawing .leaflet-interactive:not(.mv-dl-first),
  .mv-dl-drawing .leaflet-marker-icon { pointer-events:none !important; }
  .mv-dl-drawing .mv-dl-first { cursor:pointer !important; }
`;

/**
 * Een vlak tekenen door hoekpunten aan te klikken. Afsluiten: klik op het eerste punt, of
 * dubbelklik. `onChange(n)` meldt hoeveel punten er staan, `onFinish(ring)` geeft het vlak.
 *
 * Tijdens het tekenen staat zoomen met dubbelklik uit -- dat is hier 'afsluiten'.
 */
export class PolygonDrawer {
  constructor(map, { onChange = () => {}, onFinish = () => {} } = {}) {
    this.map = map;
    this.onChange = onChange;
    this.onFinish = onFinish;
    this.points = [];
    this.active = false;
    this._group = L.layerGroup();
    this._onClick = (e) => this._add(e.latlng);
    this._onMove = (e) => this._preview(e.latlng);
    this._onDbl = (e) => { L.DomEvent.stop(e); this.finish(); };
  }

  start() {
    if (this.active) return;
    this.active = true;
    this.points = [];
    this._dblZoom = this.map.doubleClickZoom.enabled();
    this.map.doubleClickZoom.disable();
    this.map.closePopup();
    L.DomUtil.addClass(this.map.getContainer(), 'mv-dl-drawing');
    this._group.addTo(this.map);
    this.map.on('click', this._onClick).on('mousemove', this._onMove).on('dblclick', this._onDbl);
    this._redraw();
  }

  /** Het laatst geplaatste punt weghalen. */
  undo() {
    if (!this.points.length) return;
    this.points.pop();
    this._redraw();
  }

  cancel() { this._stop(); }

  finish() {
    if (!this.active) return;
    // Een dubbelklik plaatst eerst twee gewone klikken: punten die (bijna) op elkaar liggen tellen als één.
    const ring = [];
    for (const p of this.points) {
      const prev = ring.at(-1);
      if (prev && this.map.latLngToContainerPoint(prev).distanceTo(this.map.latLngToContainerPoint(p)) < 5) continue;
      ring.push(p);
    }
    if (ring.length < 3) { this.points = ring; this._redraw(); return; }
    this._stop();
    this.onFinish(ring.map((p) => [p.lng, p.lat]));
  }

  _stop() {
    if (!this.active) return;
    this.active = false;
    this.map.off('click', this._onClick).off('mousemove', this._onMove).off('dblclick', this._onDbl);
    if (this._dblZoom) this.map.doubleClickZoom.enable();
    L.DomUtil.removeClass(this.map.getContainer(), 'mv-dl-drawing');
    this._group.clearLayers();
    this._group.remove();
    this.points = [];
  }

  _add(latlng) {
    this.points.push(latlng);
    this._redraw();
  }

  _redraw() {
    this._group.clearLayers();
    const style = { color: SUBLOCATION_COLOR, weight: 2, interactive: false };
    if (this.points.length > 1) L.polyline(this.points, style).addTo(this._group);
    this._rubber = L.polyline([], { ...style, dashArray: '4 4', opacity: 0.7 }).addTo(this._group);
    this.points.forEach((p, i) => {
      const first = i === 0;
      const m = L.circleMarker(p, {
        radius: first ? 6 : 4, color: SUBLOCATION_COLOR, weight: 2, fillColor: '#fff', fillOpacity: 1,
        className: first ? 'mv-dl-first' : '', interactive: first, bubblingMouseEvents: false,
      }).addTo(this._group);
      if (first) m.on('click', (e) => { L.DomEvent.stop(e); this.finish(); });
    });
    this.onChange(this.points.length);
  }

  _preview(latlng) {
    if (!this._rubber || !this.points.length) return;
    const last = this.points.at(-1);
    this._rubber.setLatLngs(this.points.length > 1 ? [last, latlng, this.points[0]] : [last, latlng]);
  }
}
